import { Role } from '@/pages/chat/type';
import { Key } from 'react';
import type { MessagePart, StructuredOutput } from './util';
import {
  applyReasoningContentToRound,
  buildAssistantParts,
  cacheToolResultOutputs,
  clearRoundStatuses,
  collectReasoningContent,
  createStreamState,
  getEventMessageId,
  getEventType,
  getMessageContent,
  getReasoningEventState,
  getTextFromBlock,
  getThinkingTime,
} from './sseEvent';
import type { StreamState } from './sseEvent';

export type HistoryMessage = {
  role?: Role;
  parts?: MessagePart[];
  structuredOutputs?: StructuredOutput[];
  answerFinished?: boolean;
  replying?: boolean;
  id: Key;
};

const hasAssistantContent = (streamState: StreamState) =>
  Boolean(
    streamState.currentContent ||
      streamState.thinkingContent ||
      streamState.statusContent ||
      streamState.structuredOutputsCache.size,
  );

export const buildHistoryMessages = (events: any[]): HistoryMessage[] => {
  const historyMessages: HistoryMessage[] = [];
  let currentAssistantMsgId: string | null = null;
  let currentStreamState = createStreamState({ replyFinished: true });
  let hasUserStarted = false;

  const flushCurrentAssistant = () => {
    if (!currentAssistantMsgId || !hasAssistantContent(currentStreamState)) {
      return;
    }

    clearRoundStatuses(currentStreamState);

    historyMessages.push({
      id: currentAssistantMsgId,
      role: Role.ASSISTANT,
      parts: buildAssistantParts(currentStreamState),
      structuredOutputs: Array.from(currentStreamState.structuredOutputsCache.values()),
      answerFinished: true,
      replying: false,
    });
  };

  for (const event of events) {
    const eventType = getEventType(event);

    if (eventType === 'USER') {
      if (hasUserStarted) {
        flushCurrentAssistant();
      }

      const firstUserBlock = getMessageContent(event)[0];
      const userContent = getTextFromBlock(firstUserBlock) || event.message || '';
      historyMessages.push({
        id: `user_${Date.now()}_${Math.random()}`,
        role: Role.USER,
        parts: [{ type: 'text', data: userContent }],
        replying: false,
      });

      hasUserStarted = true;
      currentAssistantMsgId = `assistant_${Date.now()}_${Math.random()}`;
      currentStreamState = createStreamState({ replyFinished: true });
      continue;
    }

    if (eventType === 'TOOL_RESULT') {
      cacheToolResultOutputs(event, currentStreamState.structuredOutputsCache);
      continue;
    }

    if (eventType === 'REASONING') {
      const reasoningContent = collectReasoningContent(event);
      const eventState = getReasoningEventState(event, reasoningContent);
      const thinkingTime = getThinkingTime(event);
      applyReasoningContentToRound(
        currentStreamState,
        getEventMessageId(event, currentAssistantMsgId ?? undefined),
        reasoningContent,
        eventState,
        thinkingTime,
      );
    }
  }

  flushCurrentAssistant();

  return historyMessages;
};
